import React, { useEffect, useMemo, useState } from "react";
import { getBillingConfig } from "./api/OrganizationClient";
import { BillingConfig } from "./model/backendTypes";

type Props = {
  open: boolean;
  onClose: () => void;
};

let stripePricingTableScriptPromise: Promise<void> | null = null;

function ensureStripePricingTableScript(): Promise<void> {
  if (stripePricingTableScriptPromise) return stripePricingTableScriptPromise;

  stripePricingTableScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      'script[src="https://js.stripe.com/v3/pricing-table.js"]'
    ) as HTMLScriptElement | null;

    if (existing) {
      if ((existing as any).__loaded) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Stripe script failed")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/pricing-table.js";
    script.async = true;
    script.onload = () => {
      (script as any).__loaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error("Stripe script failed"));
    document.head.appendChild(script);
  });

  return stripePricingTableScriptPromise;
}

export default function StripePricingTableModal({ open, onClose }: Props) {
  const [config, setConfig] = useState<BillingConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = await getBillingConfig();
        await ensureStripePricingTableScript();
        if (!cancelled) {
          setConfig(cfg);
          setReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load pricing table.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const pricingTable = useMemo(() => {
    if (!config || !ready) return null;

    return React.createElement("stripe-pricing-table", {
      "pricing-table-id": config.pricing_table_id,
      "publishable-key": config.publishable_key,
      "customer-email": config.customer_email ?? undefined,
      "client-reference-id": config.client_reference_id,
    } as any);
  }, [config, ready]);

  if (!open) return null;

  return (
    <div className="billing-modal__backdrop" onClick={onClose}>
      <div
        className="billing-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Upgrade"
      >
        <div className="billing-modal__header">
          <h2>Upgrade</h2>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>

        {loading && <p>Lade…</p>}
        {error && <p className="billing-panel__error">{error}</p>}
        {!loading && !error && pricingTable}
      </div>
    </div>
  );
}